import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { updateDoc, doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase-config';
import '../../Styles/ValuatorStyles/CaseDetailsComponent.css';
import '../../Styles/ValuatorStyles/WorkerList.css'
import { useAuth } from '../../contexts/AuthContext';
import { useWorker } from '../../contexts/WorkerContext';
import useNavigation from '../../hooks/useNavigation';

function CaseDetailsComponent({ caseDetails, questions, isArchived, companyID, caseId, deleteData }) {
    const { isAdmin } = useAuth();
    const { navigateTo } = useNavigation();
    const [workersDetails, setWorkersDetails] = useState([]);
    const { setCurrentWorker } = useWorker();

    const handleDelete = async () => {
      const isConfirmed = window.confirm(`Ønsker du at fjerne sag nr: ${caseId}?`);
      if (isConfirmed) {
          try {
              await deleteData(caseId);
              alert(`Sag nr: ${caseId} blev slettet.`);
              navigateTo('/valuator/cases');
          } catch (error) {
              console.error('Error during deletion:', error);
              alert(`Fejl ved sletning af sagen.`);
          }
      }
  };
    
    useEffect(() => {
      const fetchWorkersDetails = async () => {
        const workersDetailsPromises = caseDetails.attachedWorkers.map(async (workerId) => {
          const workerRef = doc(db, "users", workerId);
          const workerSnap = await getDoc(workerRef);
          if (workerSnap.exists()) {
            return { id: workerSnap.id, ...workerSnap.data() };
          } else {
            return null;
          }
        });
        const workersDetails = await Promise.all(workersDetailsPromises);
        setWorkersDetails(workersDetails.filter(Boolean));
      };

      if (caseDetails?.attachedWorkers?.length > 0) {
          fetchWorkersDetails();
      }
  }, [caseDetails?.attachedWorkers]);

    const handleArchiveToggle = async () => {
        const isConfirmed = window.confirm(
            `Er du sikker på du vil ${isArchived ? 'fjerne sagen fra arkiv?' : 'arkivere'} this case?`
        );

        if (isConfirmed) {
            try {
                const caseRef = doc(db, 'companies', companyID, 'cases', caseId);
                await updateDoc(caseRef, {
                    archived: !isArchived,
                });
                alert(`Case ${isArchived ? 'unarchived' : 'archived'} successfully!`);
                navigateTo(isArchived ? '/valuator/archived-cases' : '/valuator/cases');
            } catch (error) {
                console.error('Error during archiving/unarchiving:', error);
                alert(`Failed to ${isArchived ? 'unarchive' : 'archive'} the case.`);
            }
        }
    };

    useEffect(() => {
        const fetchAndDrawSignature = async () => {
            if (caseDetails?.path) {
                try {
                    const signatureFileRef = ref(storage, caseDetails.path);
                    const url = await getDownloadURL(signatureFileRef);
                    const response = await fetch(url);
                    const signatureData = await response.json();
                    drawSignature(signatureData);
                } catch (error) {
                    console.error('Error fetching signature data:', error);
                }
            }
        };

        fetchAndDrawSignature();
    }, [caseDetails]);

    const drawSignature = (signatureData) => {
      const canvas = document.getElementById('signatureCanvas');
      if (canvas && Array.isArray(signatureData)) {

        const maxX = Math.max(...signatureData.map(p => p.x));
        const maxY = Math.max(...signatureData.map(p => p.y));
        const minX = Math.min(...signatureData.map(p => p.x));
        const minY = Math.min(...signatureData.map(p => p.y));

        const actualWidth = maxX - minX;
        const actualHeight = maxY - minY;

        const scale = Math.min(canvas.width / actualWidth, canvas.height / actualHeight - 0.15);
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);


        const padding = 20; 
        const scaledPaddingX = padding * (actualWidth / canvas.width);
        const scaledPaddingY = padding * (actualHeight / canvas.height);


        ctx.translate(
          (canvas.width - actualWidth * scale) / 2 - minX * scale + scaledPaddingX,
          (canvas.height - actualHeight * scale) / 2 - minY * scale + scaledPaddingY
        );

        signatureData.forEach(point => {
          const { action, x, y } = point;
        
          const scaledX = (x - minX) * scale;
          const scaledY = (y - minY) * scale;

          switch(action) {
            case 'start':
              ctx.beginPath();
              ctx.moveTo(scaledX, scaledY);
              break;
            case 'move':
              ctx.lineTo(scaledX, scaledY);
              ctx.stroke();
              break;
            case 'end':
              ctx.closePath();
              break;
            default:
              break;
          }
        });

        ctx.setTransform(1, 0, 0, 1, 0, 0);
      }
    };

    const onBackClick = () => {
      navigateTo(isArchived ? '/valuator/archived-cases' : '/valuator/cases');
  };

  
    const renderRatings = () => {
      if (caseDetails && Array.isArray(caseDetails.ratings) && caseDetails.ratings.length > 0) {
        const questionObj = questions[0];
        return (
          <div className="ratings-container">
          <h1 className="main-header">Sags Nr: {caseId}</h1>
          <div className="btn-container">
            <button className="back-btn2" onClick={onBackClick}>
            Tilbage til {isArchived ? 'arkiverede' : 'aktive'} sager
          </button>
          {isAdmin && (
            <button className={`${isArchived ? 'unarchive-btn' : 'archive-btn'}`} onClick={handleArchiveToggle}>
              {isArchived ? 'Genopret sag' : 'Arkiver sag'}
            </button>
          )}
          </div>
          <h2 className="case-title">Rating</h2>
              <ul className="ratings-list">
              {caseDetails.ratings.map((rating, index) => {
                const questionKey = `question${index + 1}`;
                const questionText = questionObj[questionKey] || `Spørgsmål ${index + 1}`;
                return (
                  <li key={index} className="rating-item">
                    <div className="question-text"><strong>Spørgsmål:</strong> {questionText.text}</div>
                    <div className="rating-value"><strong>Rating:</strong> {rating}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      } else {
        return <p className="no-ratings">Ingen rating</p>;
      }
    };

  const onWorkerClick = (workerId) => {
    navigateTo(`/valuator/workers/${workerId}`);
  };


    const otherCommentClass = caseDetails.otherComment && caseDetails.otherComment.trim() !== '' 
    ? 'otherComment' 
    : 'otherComment-empty';

    return (
        <div className="case-details-container">
            {renderRatings()}
            {workersDetails.length > 0 && (
                <div className="worker-details-container">
                    <h3 className='case-title'>Medarbejder tilknyttet sagen:</h3>
                    <ul className='worker-details-list-content'>
                    {workersDetails.map((worker) => (
                      <li className="worker-list-li" key={worker.id} onClick={() => onWorkerClick(worker.id)}>
                      {worker.name}
                    </li>
                      ))}
                    </ul>
                </div>
            )}
            <h2 className="case-title">Anden kommentar og underskrift:</h2>
            <div className={otherCommentClass}>
                {caseDetails?.otherComment}
            </div>
            <canvas id="signatureCanvas" width="600" height="300" style={{ border: '1px solid black' }}></canvas>
            {isAdmin && !isArchived && (
                <button className="delete-btn" onClick={handleDelete}>
                    Slet Sag
                </button>
            )}
        </div>
    );
}

export default CaseDetailsComponent;
